<script>
import axios from '@/utils/axios';
import Swal from 'sweetalert2';

export default {
  inject: ['setLoading'],
  data() {
    return {
      title: "CPQ",
      text: "CPQ",
      items: [],
      selected_item: '',
      tiersList: [],
      loading: false,
      editing_id: null,
      form: { min_qty: '', max_qty: '', discount_percent: '' },
    };
  },
  computed: {
    text1() {
      return `Price Tiers (${this.tiersList.length})`;
    },
  },
  mounted() {
    this.fetchItems();
  },
  methods: {
    async fetchItems() {
      try {
        const res = await axios.get('/items');
        const payload = res.data.data;
        this.items = Array.isArray(payload) ? payload : (payload?.data || []);
      } catch (error) {
        this.$swalToast(error.message, 'error');
      }
    },
    resetForm() {
      this.editing_id = null;
      this.form = { min_qty: '', max_qty: '', discount_percent: '' };
    },
    async fetchTiers() {
      if (!this.selected_item) { this.tiersList = []; return; }
      this.loading = true;
      try {
        const res = await axios.get(`/item-price-tiers/${this.selected_item}`);
        this.tiersList = res.data.data || [];
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },
    editTier(row) {
      this.editing_id = row.id;
      this.form = {
        min_qty: row.min_qty,
        max_qty: row.max_qty,
        discount_percent: row.discount_percent,
      };
    },
    async saveTier() {
      if (!this.selected_item) {
        this.$swalToast('Select an item first', 'warning');
        return;
      }
      if (this.form.min_qty === '' || this.form.discount_percent === '') {
        this.$swalToast('Min Qty and Discount % are required', 'warning');
        return;
      }
      this.setLoading(true);
      try {
        const payload = {
          item_id: this.selected_item,
          min_qty: this.form.min_qty,
          max_qty: this.form.max_qty === '' ? null : this.form.max_qty,
          discount_percent: this.form.discount_percent,
        };
        if (this.editing_id) {
          await axios.put(`/item-price-tiers/${this.editing_id}`, payload);
          this.$swalToast('Tier updated');
        } else {
          await axios.post('/item-price-tiers', payload);
          this.$swalToast('Tier added');
        }
        this.resetForm();
        this.fetchTiers();
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },
    async deleteTier(row) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Delete this tier (Qty ${row.min_qty}+)?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
      });
      if (!result.isConfirmed) return;
      this.setLoading(true);
      try {
        await axios.delete(`/item-price-tiers/${row.id}`);
        this.$swalToast('Tier deleted');
        this.fetchTiers();
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
          <vue-select
            :options="items"
            label="name"
            v-model="selected_item"
            :reduce="item => item.id"
            placeholder="Select an item"
            :clearable="true"
            @update:modelValue="fetchTiers"
            style="min-width: 220px;"
          ></vue-select>
        </div>
      </div>

      <div class="card border-0 shadow-sm mb-0" style="border-radius:14px;">
        <div class="card-body px-3 pt-3 pb-3">

          <div v-if="!selected_item" class="ipt-empty">
            <i class="ti ti-discount-2"></i>
            <p class="mb-0">Select an item to view its price tiers</p>
          </div>

          <template v-else>
            <div class="ipt-list mb-4">
              <div v-if="loading" class="ipt-empty">
                <span class="spinner-border spinner-border-sm"></span>
              </div>
              <div v-else-if="!tiersList.length" class="ipt-empty">
                <i class="ti ti-discount-2"></i>
                <p class="mb-0">No price tiers added yet</p>
                <span>Use the form below to set up your first bulk discount</span>
              </div>

              <div v-else class="ipt-row ipt-row-head">
                <div class="ipt-col ipt-col-qty">Quantity Range</div>
                <div class="ipt-col ipt-col-discount">Discount</div>
                <div class="ipt-col ipt-col-action text-end">Action</div>
              </div>

              <div v-for="row in tiersList" :key="row.id"
                class="ipt-row" :class="{ 'ipt-row-editing': editing_id === row.id }">
                <div class="ipt-col ipt-col-qty">
                  <span class="ipt-qty-pill">
                    {{ row.min_qty }}{{ row.max_qty ? ' – ' + row.max_qty : '+' }}
                  </span>
                </div>
                <div class="ipt-col ipt-col-discount">
                  <span class="badge ipt-badge-discount">{{ row.discount_percent }}% off</span>
                </div>
                <div class="ipt-col ipt-col-action text-end">
                  <button type="button" class="ipt-icon-btn" @click="editTier(row)" title="Edit">
                    <i class="ti ti-edit"></i>
                  </button>
                  <button type="button" class="ipt-icon-btn ipt-icon-btn-danger" @click="deleteTier(row)" title="Delete">
                    <i class="ti ti-trash"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="ipt-form-card">
              <div class="ipt-form-title">
                <i class="ti" :class="editing_id ? 'ti-edit' : 'ti-circle-plus'"></i>
                {{ editing_id ? 'Edit Price Tier' : 'Add New Price Tier' }}
              </div>

              <div class="row g-3 align-items-end">
                <div class="col-6 col-md-3">
                  <label class="form-label ipt-label">Min Qty</label>
                  <input type="number" class="form-control" v-model.number="form.min_qty" placeholder="e.g. 10" />
                </div>
                <div class="col-6 col-md-3">
                  <label class="form-label ipt-label">Max Qty</label>
                  <input type="number" class="form-control" v-model.number="form.max_qty" placeholder="blank = no limit" />
                </div>
                <div class="col-6 col-md-3">
                  <label class="form-label ipt-label">Discount %</label>
                  <input type="number" step="0.01" class="form-control" v-model.number="form.discount_percent" placeholder="e.g. 5" />
                </div>
                <div class="col-6 col-md-3 d-flex gap-2">
                  <button type="button" class="btn btn-primary flex-grow-1" @click="saveTier">
                    {{ editing_id ? 'Update' : 'Add' }}
                  </button>
                  <button v-if="editing_id" type="button" class="btn btn-outline-secondary" @click="resetForm" title="Cancel edit">
                    <i class="ti ti-x"></i>
                  </button>
                </div>
              </div>
              <div class="ipt-hint mt-2">
                <i class="ti ti-info-circle"></i>
                Leave Max Qty blank for an open-ended tier (e.g. "50 and above").
              </div>
            </div>
          </template>

        </div>
      </div>
    </div>
    <app-footer></app-footer>
  </div>
</template>

<style scoped>
.ipt-empty {
  text-align: center;
  padding: 32px 16px;
  border: 1.5px dashed #e2e8f0;
  border-radius: 12px;
  color: #94a3b8;
}
.ipt-empty i {
  font-size: 28px;
  display: block;
  margin-bottom: 8px;
  color: #cbd5e1;
}
.ipt-empty p {
  font-size: 13.5px;
  font-weight: 600;
  color: #64748b;
}
.ipt-empty span {
  font-size: 12px;
}
.ipt-list {
  border: 1px solid #eef2f7;
  border-radius: 12px;
  overflow: hidden;
}
.ipt-row {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  transition: background .12s;
}
.ipt-row:last-child { border-bottom: none; }
.ipt-row:not(.ipt-row-head):hover { background: #f8fafc; }
.ipt-row-head {
  background: #f8fafc;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #94a3b8;
  padding-top: 10px;
  padding-bottom: 10px;
}
.ipt-row-editing { background: #eef2ff; }

.ipt-col-qty      { flex: 1 1 40%; }
.ipt-col-discount { flex: 1 1 35%; }
.ipt-col-action   { flex: 0 0 25%; }

.ipt-qty-pill {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}
.ipt-badge-discount {
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
}
.ipt-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  transition: all .12s;
}
.ipt-icon-btn:hover { background: #f1f5f9; border-color: #cbd5e1; }
.ipt-icon-btn-danger:hover { background: #fef2f2; border-color: #fecaca; color: #ef4444; }

.ipt-form-card {
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 16px;
}
.ipt-form-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.ipt-form-title i { color: #6366f1; }
.ipt-label {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
}
.ipt-hint {
  font-size: 11.5px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 5px;
}
</style>